/**
 * Neon Customizer Skeleton Loader
 * Independent static script for skeleton loading in neon customizer
 */

class NeonCustomizerSkeleton {
  constructor(container) {
    this.container = container;
    this.init();
  }

  init() {
    this.injectStyles();
    this.renderSkeleton();
    this.setupCleanup();
  }

  injectStyles() {
    // Find the shadow root by traversing up from container
    let currentElement = this.container;
    let shadowRoot = null;

    while (currentElement && !shadowRoot) {
      if (currentElement.parentNode && currentElement.parentNode.host) {
        shadowRoot = currentElement.parentNode;
      } else if (currentElement.parentNode && currentElement.parentNode.nodeType === 11) { // Shadow root
        shadowRoot = currentElement.parentNode;
      }
      currentElement = currentElement.parentNode;
    }

    if (shadowRoot.querySelector('#neon-customizer-skeleton-styles')) {
      return; // Already injected
    }

    const skeletonStyles = document.createElement('style');
    skeletonStyles.id = 'neon-customizer-skeleton-styles';
    skeletonStyles.textContent = `
      .skeleton {
        background: linear-gradient(90deg, #e2e8f0 25%, #cbd5e1 50%, #e2e8f0 75%);
        background-size: 200% 100%;
        animation: skeleton-pulse 1.5s infinite;
        border-radius: 4px;
      }

      @keyframes skeleton-pulse {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }

      /* Desktop Layout */
      .neon-customizer-skeleton {
        display: flex;
        height: 100vh;
        width: 100vw;
      }

      .preview-panel {
        flex: 0 0 65%;
        display: flex;
        flex-direction: column;
        background: #f1f5f9;
      }

      .preview-main {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 40px;
      }

      .preview-box {
        width: 100%;
        height: 200px;
        max-width: 500px;
      }

      .preview-footer {
        height: 80px;
        padding: 0 40px 30px;
        display: flex;
        justify-content: center;
        gap: 20px;
      }

      .preview-btn {
        height: 44px;
        width: 160px;
      }

      .config-panel {
        flex: 0 0 35%;
        background: #ffffff;
        padding: 24px;
        display: flex;
        flex-direction: column;
        gap: 24px;
      }

      .config-item {
        height: 48px;
      }

      .config-item.tall {
        height: 120px;
      }

      .config-item.short {
        height: 36px;
      }

      .config-row {
        display: flex;
        gap: 8px;
      }

      .config-row .config-item {
        flex: 1;
        height: 44px;
      }

      .config-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 12px;
      }

      .config-grid .config-item {
        height: 48px;
        width: 48px;
        border-radius: 50%;
      }

      .config-footer {
        margin-top: auto;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-top: 16px;
        border-top: 1px solid #e2e8f0;
      }

      .config-footer .config-item:first-child {
        width: 80px;
        height: 24px;
      }

      .config-footer .config-item:last-child {
        width: 160px;
        height: 48px;
      }

      /* Mobile Layout */
      @media (max-width: 768px) {
        .neon-customizer-skeleton {
          flex-direction: column;
          height: auto;
          min-height: 100vh;
        }

        .preview-panel {
          flex: none;
          height: 400px;
        }

        .preview-main {
          padding: 20px;
        }

        .preview-box {
          height: 150px;
        }

        .preview-footer {
          padding: 0 20px 20px;
          gap: 16px;
        }

        .preview-btn {
          width: 140px;
          height: 40px;
        }

        .config-panel {
          padding: 20px;
          gap: 20px;
        }

        .config-grid {
          grid-template-columns: repeat(6, 1fr);
          gap: 8px;
        }

        .config-grid .config-item {
          width: 40px;
          height: 40px;
        }

        .config-footer {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: #ffffff;
          padding: 16px 20px;
          margin: 0 -20px -20px -20px;
          border-top: 1px solid #e2e8f0;
        }
      }
    `;

    shadowRoot.appendChild(skeletonStyles);
  }

  renderSkeleton() {
    // Render skeleton HTML
    this.container.innerHTML = `
      <div class="neon-customizer-skeleton">
        <!-- Preview Panel -->
        <div class="preview-panel">
          <div class="preview-main">
            <div class="preview-box skeleton"></div>
          </div>
          <div class="preview-footer">
            <div class="preview-btn skeleton"></div>
            <div class="preview-btn skeleton"></div>
          </div>
        </div>

        <!-- Config Panel -->
        <div class="config-panel">
          <!-- Header -->
          <div class="config-item skeleton"></div>

          <!-- Text input -->
          <div class="config-item tall skeleton"></div>

          <!-- Font selector -->
          <div class="config-item skeleton"></div>

          <!-- Color grid -->
          <div class="config-grid">
            <div class="config-item skeleton"></div>
            <div class="config-item skeleton"></div>
            <div class="config-item skeleton"></div>
            <div class="config-item skeleton"></div>
            <div class="config-item skeleton"></div>
            <div class="config-item skeleton"></div>
            <div class="config-item skeleton"></div>
            <div class="config-item skeleton"></div>
            <div class="config-item skeleton"></div>
            <div class="config-item skeleton"></div>
            <div class="config-item skeleton"></div>
            <div class="config-item skeleton"></div>
          </div>

          <!-- Tube toggles -->
          <div class="config-row">
            <div class="config-item skeleton"></div>
            <div class="config-item skeleton"></div>
          </div>

          <!-- Size buttons -->
          <div class="config-row">
            <div class="config-item skeleton"></div>
            <div class="config-item skeleton"></div>
            <div class="config-item skeleton"></div>
          </div>

          <!-- Footer -->
          <div class="config-footer">
            <div class="config-item skeleton"></div>
            <div class="config-item skeleton"></div>
          </div>
        </div>
      </div>
    `;
  }

  setupCleanup() {
    // Clean up skeleton when app loads
    window.addEventListener('load', () => {
      // Small delay to ensure app has initialized
      setTimeout(() => {
        // Find the skeleton element within the container
        const skeletonElement = this.container.querySelector('.neon-customizer-skeleton');
        if (skeletonElement) {
          // Clear skeleton content - app should have replaced it
          const skeletonBoxes = skeletonElement.querySelectorAll('.skeleton');
          skeletonBoxes.forEach(box => box.remove());

          // Remove the entire skeleton container if it's still there
          if (this.container.contains(skeletonElement)) {
            this.container.innerHTML = '';
          }
        }
      }, 100);
    });
  }

  destroy() {
    // Clean up if needed - find shadow root by traversing up
    let currentElement = this.container;
    let shadowRoot = null;

    // Traverse up to find shadow root
    while (currentElement && !shadowRoot) {
      if (currentElement.parentNode && currentElement.parentNode.host) {
        shadowRoot = currentElement.parentNode;
      }
      currentElement = currentElement.parentNode;
    }

    if (shadowRoot) {
      const styles = shadowRoot.querySelector('#neon-customizer-skeleton-styles');
      if (styles) {
        styles.remove();
      }
    }
  }
}

// Export for use in other files
if (typeof module !== 'undefined' && module.exports) {
  module.exports = NeonCustomizerSkeleton;
} else if (typeof window !== 'undefined') {
  window.NeonCustomizerSkeleton = NeonCustomizerSkeleton;
}
